import { SignalingClient } from './signaling';

export class WebRTCManager {
  private localStream: MediaStream | null = null;
  private screenStream: MediaStream | null = null;
  private virtualVideoTimer: any = null;
  private virtualVideoTrack: MediaStreamTrack | null = null;
  private signaling: SignalingClient | null = null;
  private myParticipantId: string = '';
  private displayName: string = '';
  private iceServers: RTCIceServer[] = [];

  // P2P Mesh connections keyed by remote peer ID
  private peers: Map<string, RTCPeerConnection> = new Map();
  private peerSenders: Map<string, { audio?: RTCRtpSender; video?: RTCRtpSender; screen?: RTCRtpSender; screenAudio?: RTCRtpSender }> = new Map();
  private pendingCandidates: Map<string, RTCIceCandidateInit[]> = new Map();
  private remoteStreams: Map<string, MediaStream> = new Map();

  // Negotiation reliability: concurrent connectToPeer calls for the same peer
  // must not each create an offer (room_state AND participant_joined both fire
  // for a new peer), and an offer whose answer never arrives must be re-sent.
  private offerInFlight: Map<string, Promise<void>> = new Map();
  private answerReceived: Map<string, boolean> = new Map();
  private offerWatchdog: Map<string, any> = new Map();

  // Legacy single peer connection for SFU fallback
  private sfuPC: RTCPeerConnection | null = null;

  public onRemoteStream?: (participantId: string, stream: MediaStream, track: MediaStreamTrack, isScreen: boolean) => void;
  public onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  public onScreenShareEnded?: () => void;

  public async init(
    iceServers: RTCIceServer[],
    signaling: SignalingClient,
    participantId: string = '',
    displayName: string = ''
  ) {
    this.signaling = signaling;
    this.myParticipantId = participantId;
    this.displayName = displayName;

    // Re-init (e.g. after waiting-room admission) must not keep peer connections
    // created before iceServers/local media were ready.
    for (const pc of this.peers.values()) {
      try { pc.close(); } catch (_) {}
    }
    this.peers.clear();
    this.peerSenders.clear();
    this.pendingCandidates.clear();
    this.offerInFlight.clear();
    this.answerReceived.clear();
    for (const timer of this.offerWatchdog.values()) clearTimeout(timer);
    this.offerWatchdog.clear();

    const defaultTurnServers: RTCIceServer[] = [
      {
        urls: [
          'stun:stun.l.google.com:19302',
          'stun:stun1.l.google.com:19302',
          'stun:stun2.l.google.com:19302',
          'stun:stun3.l.google.com:19302',
          'stun:stun4.l.google.com:19302',
          'stun:stun.cloudflare.com:3478',
        ],
      },
      {
        // OpenRelay public TURN (metered.ca discontinued the free relay —
        // allocations fail with 401). Kept only as a last-resort fallback; the
        // server-provided list (VERVE_ICE_SERVERS_JSON) takes precedence so a
        // real TURN relay can be plugged in without a client rebuild.
        urls: [
          'turn:openrelay.metered.ca:80',
          'turn:openrelay.metered.ca:443',
          'turn:openrelay.metered.ca:443?transport=tcp',
        ],
        username: 'openrelay',
        credential: 'openrelay',
      },
    ];

    this.iceServers = (iceServers && iceServers.length > 0)
      ? iceServers
      : defaultTurnServers;

    // Setup direct P2P mesh signaling handlers
    this.signaling.on('peer_offer', async (payload: { sender_id: string; sdp: RTCSessionDescriptionInit }) => {
      const peerId = payload.sender_id;
      if (!peerId || peerId === this.myParticipantId) return;

      const isPolite = this.myParticipantId < peerId;
      const pc = this.getOrCreatePeerConnection(peerId, false);

      try {
        const offerCollision = pc.signalingState !== 'stable';
        if (offerCollision) {
          if (!isPolite) {
            console.warn(`Impolite peer ${this.myParticipantId} ignoring colliding offer from ${peerId}`);
            return;
          }
          await pc.setLocalDescription({ type: 'rollback' });
        }

        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        await this.drainPendingCandidates(peerId, pc);

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.signaling?.send('peer_answer', {
          target_id: peerId,
          sdp: answer,
        });
      } catch (err) {
        console.error(`Error handling peer_offer from ${peerId}:`, err);
      }
    });

    this.signaling.on('peer_answer', async (payload: { sender_id: string; sdp: RTCSessionDescriptionInit }) => {
      const peerId = payload.sender_id;
      this.answerReceived.set(peerId, true);
      const timer = this.offerWatchdog.get(peerId);
      if (timer) {
        clearTimeout(timer);
        this.offerWatchdog.delete(peerId);
      }

      const pc = this.peers.get(peerId);
      if (pc && pc.signalingState === 'have-local-offer') {
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
          await this.drainPendingCandidates(peerId, pc);
        } catch (err) {
          console.error(`Error setting remote description for peer_answer from ${peerId}:`, err);
        }
      } else {
        // A duplicate or raced answer. Previously this was a silent drop that
        // could strand the offerer in have-local-offer forever — now the offer
        // watchdog (armed on every offer send) re-issues the negotiation.
        console.warn(`peer_answer from ${peerId} ignored in signalingState=${pc?.signalingState}; offer watchdog remains active`);
      }
    });

    this.signaling.on('peer_ice_candidate', async (payload: { sender_id: string; candidate: RTCIceCandidateInit }) => {
      const peerId = payload.sender_id;
      if (!peerId || !payload.candidate) return;

      const pc = this.peers.get(peerId);
      if (!pc || !pc.remoteDescription) {
        const queue = this.pendingCandidates.get(peerId) || [];
        queue.push(payload.candidate);
        this.pendingCandidates.set(peerId, queue);
        return;
      }

      try {
        await pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
      } catch (err) {
        console.warn(`Failed to add ICE candidate from ${peerId}:`, err);
      }
    });

    // SFU fallback listeners
    this.signaling.on('sfu_offer', async (payload: { sdp: RTCSessionDescriptionInit }) => {
      if (!this.sfuPC) return;
      try {
        await this.sfuPC.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        const answer = await this.sfuPC.createAnswer();
        await this.sfuPC.setLocalDescription(answer);
        this.signaling?.send('sfu_answer', { sdp: answer });
      } catch (err) {
        console.warn('SFU offer handling skipped:', err);
      }
    });

    this.signaling.on('sfu_answer', async (payload: { sdp: RTCSessionDescriptionInit }) => {
      if (!this.sfuPC) return;
      try {
        await this.sfuPC.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      } catch (err) {
        console.warn('SFU answer handling skipped:', err);
      }
    });

    this.signaling.on('ice_candidate', async (payload: { candidate: RTCIceCandidateInit }) => {
      if (!this.sfuPC) return;
      try {
        await this.sfuPC.addIceCandidate(new RTCIceCandidate(payload.candidate));
      } catch (err) {
        console.warn('SFU ICE candidate skipped:', err);
      }
    });
  }

  public connectToPeer(peerId: string): Promise<void> {
    if (!peerId || peerId === this.myParticipantId) return Promise.resolve();

    // Serialize negotiation per peer: room_state and participant_joined both
    // trigger connectToPeer for the same new peer, and two racing offers leave
    // one side stranded in have-local-offer when the duplicate answer is dropped.
    const inFlight = this.offerInFlight.get(peerId);
    if (inFlight) return inFlight;

    const task = this.doConnectToPeer(peerId).finally(() => {
      this.offerInFlight.delete(peerId);
    });
    this.offerInFlight.set(peerId, task);
    return task;
  }

  private async doConnectToPeer(peerId: string) {
    // Deterministic Initiator Rule: Peer with greater ID initiates offer
    const isInitiator = this.myParticipantId > peerId;

    // A previously closed or permanently failed connection must be rebuilt,
    // not reused (getOrCreatePeerConnection would hand back the dead object).
    const existing = this.peers.get(peerId);
    if (existing && (existing.connectionState === 'closed' || existing.connectionState === 'failed')) {
      this.removePeer(peerId);
    }

    const pc = this.getOrCreatePeerConnection(peerId, isInitiator);

    if (isInitiator && pc.signalingState === 'stable' && pc.connectionState !== 'connected') {
      try {
        this.answerReceived.set(peerId, false);
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        });
        await pc.setLocalDescription(offer);
        this.signaling?.send('peer_offer', {
          target_id: peerId,
          sdp: offer,
        });
        this.armOfferWatchdog(peerId);
      } catch (err) {
        console.error(`Failed to initiate offer to peer ${peerId}:`, err);
      }
    }
  }

  // If the answer never arrives (lost POST, target registered late), the offer
  // side would otherwise wait forever in have-local-offer. Re-issue the offer;
  // the remote's perfect-negotiation handling applies it safely.
  private armOfferWatchdog(peerId: string) {
    const prev = this.offerWatchdog.get(peerId);
    if (prev) clearTimeout(prev);

    const timer = setTimeout(() => {
      this.offerWatchdog.delete(peerId);
      const pc = this.peers.get(peerId);
      if (!pc || this.answerReceived.get(peerId)) return;
      if (pc.connectionState === 'closed') return;
      if (pc.signalingState !== 'have-local-offer' && pc.signalingState !== 'stable') return;

      console.warn(`No peer_answer from ${peerId} — re-issuing offer (watchdog)`);
      (async () => {
        try {
          if (pc.signalingState === 'have-local-offer') {
            // Re-send the exact current local description (no new offer needed)
            this.signaling?.send('peer_offer', {
              target_id: peerId,
              sdp: pc.localDescription,
            });
          } else if (pc.signalingState === 'stable') {
            const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
            await pc.setLocalDescription(offer);
            this.signaling?.send('peer_offer', { target_id: peerId, sdp: offer });
          }
          this.armOfferWatchdog(peerId);
        } catch (err) {
          console.warn(`Offer watchdog re-issue failed for ${peerId}:`, err);
        }
      })();
    }, 8000);

    this.offerWatchdog.set(peerId, timer);
  }

  public getPeers(): Map<string, RTCPeerConnection> {
    return this.peers;
  }

  public removePeer(peerId: string) {
    const pc = this.peers.get(peerId);
    if (pc) {
      pc.close();
      this.peers.delete(peerId);
    }
    this.peerSenders.delete(peerId);
    this.pendingCandidates.delete(peerId);
    this.answerReceived.delete(peerId);
    // Drop any stale in-flight negotiation task so a prompt rejoin of the same
    // participant id gets a fresh connectToPeer instead of a swallowed no-op.
    this.offerInFlight.delete(peerId);
    const timer = this.offerWatchdog.get(peerId);
    if (timer) {
      clearTimeout(timer);
      this.offerWatchdog.delete(peerId);
    }
  }

  private getOrCreatePeerConnection(peerId: string, _isInitiator: boolean = false): RTCPeerConnection {
    let pc = this.peers.get(peerId);
    if (pc) return pc;

    pc = new RTCPeerConnection({
      iceServers: this.iceServers,
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
    });
    this.peers.set(peerId, pc);

    const senders: { audio?: RTCRtpSender; video?: RTCRtpSender; screen?: RTCRtpSender; screenAudio?: RTCRtpSender } = {};
    this.peerSenders.set(peerId, senders);

    // Auto-renegotiation: track additions after the initial handshake (screen
    // share, late camera, unmute re-add) must reach the remote side. This is
    // what lets a peer who joins while someone is already screen sharing
    // receive that stream when the deterministic initiator's initial offer
    // could not carry the screen m-line. Skipped while connectToPeer holds the
    // per-peer negotiation slot; browsers re-fire negotiationneeded once
    // signaling returns to stable, so nothing is lost.
    pc.onnegotiationneeded = () => {
      if (pc.signalingState === 'stable' && pc.connectionState !== 'closed' && !this.offerInFlight.has(peerId)) {
        this.renegotiatePeer(peerId, pc);
      }
    };

    // 1. Add existing local audio and camera tracks
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        try {
          const sender = pc.addTrack(track, this.localStream);
          if (track.kind === 'audio') senders.audio = sender;
          else if (track.kind === 'video') senders.video = sender;
        } catch (e) {
          console.warn(`Error adding local track to peer ${peerId}:`, e);
        }
      }
    }

    // 2. Add screen track if currently sharing
    if (this.screenStream) {
      const screenTrack = this.screenStream.getVideoTracks()[0];
      if (screenTrack) {
        try {
          senders.screen = pc.addTrack(screenTrack, this.screenStream);
        } catch (e) {
          console.warn(`Error adding screen track to peer ${peerId}:`, e);
        }
      }
      const screenAudioTrack = this.screenStream.getAudioTracks()[0];
      if (screenAudioTrack) {
        try {
          senders.screenAudio = pc.addTrack(screenAudioTrack, this.screenStream);
        } catch (e) {
          console.warn(`Error adding screen audio track to peer ${peerId}:`, e);
        }
      }
    }

    // 3. Trickle ICE Candidate Handler
    pc.onicecandidate = (event) => {
      if (event.candidate && this.signaling) {
        this.signaling.send('peer_ice_candidate', {
          target_id: peerId,
          candidate: event.candidate,
        });
      }
    };

    // 4. Remote Track Handler
    pc.ontrack = (event) => {
      const [stream] = event.streams;
      const targetStream = stream || new MediaStream([event.track]);
      this.remoteStreams.set(event.track.id, targetStream);

      const isScreen = Boolean(
        event.track.kind === 'video' && (
          event.track.label.toLowerCase().includes('screen') ||
          event.track.label.toLowerCase().includes('display') ||
          (stream && stream.id.toLowerCase().includes('screen'))
        )
      );

      if (this.onRemoteStream) {
        this.onRemoteStream(peerId, targetStream, event.track, isScreen);
      }

      // Re-trigger when first keyframe / packet arrives
      event.track.onunmute = () => {
        if (this.onRemoteStream) {
          this.onRemoteStream(peerId, targetStream, event.track, isScreen);
        }
      };
    };

    // 5. Connection State
    pc.onconnectionstatechange = () => {
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(pc.connectionState);
      }
    };

    // 6. ICE Failure Recovery: when the ice transport hard-fails (dead TURN,
    // NAT rebinding, network switch), restart ICE from the deterministic
    // initiator side. The polite peer answers the restart offer as usual.
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed') {
        if (this.myParticipantId > peerId) {
          console.warn(`ICE failed with ${peerId} — attempting ICE restart`);
          try { pc.restartIce(); } catch (_) {}
          setTimeout(() => {
            if (this.peers.get(peerId) === pc && pc.signalingState === 'stable') {
              this.renegotiatePeer(peerId, pc, true);
            }
          }, 500);
        }
      }
    };

    return pc;
  }

  private async drainPendingCandidates(peerId: string, pc: RTCPeerConnection) {
    const queued = this.pendingCandidates.get(peerId);
    if (queued && queued.length > 0) {
      this.pendingCandidates.delete(peerId);
      for (const cand of queued) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(cand));
        } catch (e) {
          console.warn(`Error applying queued ICE candidate:`, e);
        }
      }
    }
  }

  public async getLocalMedia(videoInputId?: string, audioInputId?: string): Promise<MediaStream> {
    if (this.localStream && this.localStream.getTracks().some(t => t.readyState === 'live')) {
      return this.localStream;
    }

    let audioTrack: MediaStreamTrack | null = null;
    let videoTrack: MediaStreamTrack | null = null;

    // 1. Try Microphone capture
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: audioInputId
          ? { deviceId: { exact: audioInputId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
          : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      audioTrack = audioStream.getAudioTracks()[0] || null;
    } catch (err) {
      console.warn('Microphone capture failed, using synthetic audio fallback:', err);
      audioTrack = this.createSyntheticAudioTrack();
    }

    // 2. Try Webcam capture
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: videoInputId
          ? { deviceId: { exact: videoInputId }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
          : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
      });
      videoTrack = videoStream.getVideoTracks()[0] || null;
    } catch (err) {
      console.warn('Webcam capture failed, activating Solveran Virtual HD Camera fallback:', err);
      try {
        videoTrack = this.getOrCreateVirtualVideoTrack();
      } catch (fallbackErr) {
        console.warn('Virtual video track creation failed:', fallbackErr);
      }
    }

    const tracks: MediaStreamTrack[] = [];
    if (audioTrack) tracks.push(audioTrack);
    if (videoTrack) tracks.push(videoTrack);

    this.localStream = new MediaStream(tracks);
    return this.localStream;
  }

  public setLocalStream(stream: MediaStream) {
    this.localStream = stream;
  }

  public publishTracks(stream: MediaStream) {
    this.localStream = stream;

    // Add to all active peer connections; renegotiate when a track was newly
    // added to an already-negotiated peer (tracks added after the initial
    // handshake are otherwise never known to the remote side).
    for (const [peerId, pc] of this.peers.entries()) {
      const senders = this.peerSenders.get(peerId) || {};
      let added = false;
      for (const track of stream.getTracks()) {
        try {
          const sender = pc.addTrack(track, stream);
          if (track.kind === 'audio') senders.audio = sender;
          else if (track.kind === 'video') senders.video = sender;
          added = true;
        } catch (e) {
          console.warn(`Track already added to peer ${peerId}:`, e);
        }
      }
      this.peerSenders.set(peerId, senders);
      if (added) {
        this.renegotiatePeer(peerId, pc);
      }
    }
  }

  public async startInitialNegotiation() {
    // Negotiate with any existing peers where myParticipantId is initiator
    for (const [peerId, pc] of this.peers.entries()) {
      if (this.myParticipantId > peerId && pc.signalingState === 'stable') {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          this.signaling?.send('peer_offer', { target_id: peerId, sdp: offer });
        } catch (err) {
          console.warn(`Failed initial negotiation to ${peerId}:`, err);
        }
      }
    }
  }

  public async startScreenShare(): Promise<MediaStream | null> {
    try {
      if (navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function') {
        try {
          this.screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: { cursor: 'always' } as MediaTrackConstraints,
            audio: true,
          });
        } catch (promptErr: any) {
          console.warn('Native getDisplayMedia aborted or denied, activating presentation stream:', promptErr);
          this.screenStream = this.createPresentationCanvasStream();
        }
      } else {
        this.screenStream = this.createPresentationCanvasStream();
      }
    } catch (err) {
      console.warn('Screen share capture failed, falling back to presentation canvas:', err);
      this.screenStream = this.createPresentationCanvasStream();
    }

    if (!this.screenStream) return null;

    const screenTrack = this.screenStream.getVideoTracks()[0];
    const screenAudioTrack = this.screenStream.getAudioTracks()[0];
    if (screenTrack) {
      screenTrack.onended = () => {
        this.stopScreenShare();
      };

      // Broadcast screen video and screen audio tracks to all peers
      this.broadcastScreenTrack(screenTrack, screenAudioTrack);
    }

    return this.screenStream;
  }

  public async stopScreenShare() {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(t => t.stop());
      this.screenStream = null;
    }

    // Remove screen track and screen audio track from all peers
    for (const [peerId, pc] of this.peers.entries()) {
      const senders = this.peerSenders.get(peerId);
      let needsRenegotiate = false;
      if (senders?.screen && pc.getSenders().includes(senders.screen)) {
        try {
          pc.removeTrack(senders.screen);
          senders.screen = undefined;
          needsRenegotiate = true;
        } catch (e) {
          console.warn(`Error removing screen track on peer ${peerId}:`, e);
        }
      }
      if (senders?.screenAudio && pc.getSenders().includes(senders.screenAudio)) {
        try {
          pc.removeTrack(senders.screenAudio);
          senders.screenAudio = undefined;
          needsRenegotiate = true;
        } catch (e) {
          console.warn(`Error removing screen audio track on peer ${peerId}:`, e);
        }
      }
      if (needsRenegotiate) {
        this.renegotiatePeer(peerId, pc);
      }
    }

    if (this.onScreenShareEnded) {
      this.onScreenShareEnded();
    }
  }

  public async toggleAudio(muted: boolean): Promise<MediaStream | null> {
    if (!this.localStream) {
      this.localStream = new MediaStream();
    }

    let liveAudio = this.localStream.getAudioTracks().find(t => t.readyState === 'live');

    if (muted) {
      if (liveAudio) {
        liveAudio.enabled = false;
      }
      this.localStream.getAudioTracks().forEach(t => { t.enabled = false; });
      return this.localStream;
    }

    // Unmute requested:
    if (liveAudio) {
      liveAudio.enabled = true;
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        const track = stream.getAudioTracks()[0];
        if (track) {
          track.enabled = true;
          this.localStream.addTrack(track);
          liveAudio = track;
        }
      } catch (err) {
        console.warn('Microphone capture on unmute failed; using synthetic audio:', err);
        const synth = this.createSyntheticAudioTrack();
        if (synth) {
          this.localStream.addTrack(synth);
          liveAudio = synth;
        }
      }
    }

    if (liveAudio) {
      this.broadcastTrackUpdate(liveAudio, 'audio');
    }

    return this.localStream;
  }

  public async toggleVideo(muted: boolean): Promise<MediaStream | null> {
    if (!this.localStream) {
      this.localStream = new MediaStream();
    }

    let liveVideo = this.localStream.getVideoTracks().find(t => t.readyState === 'live');

    if (muted) {
      if (liveVideo) {
        liveVideo.enabled = false;
      }
      this.localStream.getVideoTracks().forEach(t => { t.enabled = false; });
      return this.localStream;
    }

    // Unmute requested:
    if (liveVideo) {
      liveVideo.enabled = true;
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        });
        const track = stream.getVideoTracks()[0];
        if (track) {
          track.enabled = true;
          this.localStream.addTrack(track);
          liveVideo = track;
        }
      } catch (err) {
        console.warn('Physical camera unavailable on unmute, activating Virtual HD Camera:', err);
        const virtualTrack = this.getOrCreateVirtualVideoTrack();
        if (virtualTrack) {
          virtualTrack.enabled = true;
          this.localStream.addTrack(virtualTrack);
          liveVideo = virtualTrack;
        }
      }
    }

    if (liveVideo) {
      this.broadcastTrackUpdate(liveVideo, 'video');
    }

    return this.localStream;
  }

  public setAudioMuted(muted: boolean) {
    this.toggleAudio(muted).catch(() => {});
  }

  public setVideoMuted(muted: boolean) {
    this.toggleVideo(muted).catch(() => {});
  }

  public isScreenSharing(): boolean {
    return this.screenStream !== null;
  }

  private broadcastTrackUpdate(track: MediaStreamTrack, kind: 'audio' | 'video') {
    for (const [peerId, pc] of this.peers.entries()) {
      const senders = this.peerSenders.get(peerId);
      const sender = kind === 'audio' ? senders?.audio : senders?.video;

      if (sender && pc.getSenders().includes(sender)) {
        sender.replaceTrack(track).catch(err => {
          console.warn(`replaceTrack failed for ${kind} on peer ${peerId}:`, err);
        });
      } else if (this.localStream) {
        try {
          const newSender = pc.addTrack(track, this.localStream);
          if (senders) {
            if (kind === 'audio') senders.audio = newSender;
            else senders.video = newSender;
          }
          this.renegotiatePeer(peerId, pc);
        } catch (e) {
          console.warn(`addTrack failed for ${kind} on peer ${peerId}:`, e);
        }
      }
    }
  }

  private broadcastScreenTrack(screenTrack: MediaStreamTrack, screenAudioTrack?: MediaStreamTrack) {
    for (const [peerId, pc] of this.peers.entries()) {
      const senders = this.peerSenders.get(peerId);
      if (senders?.screen && pc.getSenders().includes(senders.screen)) {
        senders.screen.replaceTrack(screenTrack).catch(() => {});
      } else if (this.screenStream) {
        try {
          const sender = pc.addTrack(screenTrack, this.screenStream);
          if (senders) senders.screen = sender;
        } catch (e) {
          console.warn(`Failed to add screen track to peer ${peerId}:`, e);
        }
      }

      if (screenAudioTrack && this.screenStream) {
        if (senders?.screenAudio && pc.getSenders().includes(senders.screenAudio)) {
          senders.screenAudio.replaceTrack(screenAudioTrack).catch(() => {});
        } else {
          try {
            const aSender = pc.addTrack(screenAudioTrack, this.screenStream);
            if (senders) senders.screenAudio = aSender;
          } catch (e) {
            console.warn(`Failed to add screen audio track to peer ${peerId}:`, e);
          }
        }
      }

      this.renegotiatePeer(peerId, pc);
    }
  }

  private async renegotiatePeer(peerId: string, pc: RTCPeerConnection, iceRestart: boolean = false) {
    if (pc.signalingState !== 'stable') return;
    try {
      const offer = await pc.createOffer(iceRestart ? { iceRestart: true } : {});
      await pc.setLocalDescription(offer);
      this.signaling?.send('peer_offer', {
        target_id: peerId,
        sdp: offer,
      });
      this.answerReceived.set(peerId, false);
      this.armOfferWatchdog(peerId);
    } catch (e) {
      console.warn(`Renegotiation failed with peer ${peerId}:`, e);
    }
  }

  private getOrCreateVirtualVideoTrack(): MediaStreamTrack {
    if (this.virtualVideoTrack && this.virtualVideoTrack.readyState === 'live') {
      return this.virtualVideoTrack;
    }

    const c = document.createElement('canvas');
    c.width = 1280;
    c.height = 720;
    const ctx = c.getContext('2d');

    let frame = 0;
    const name = this.displayName || 'Verve Participant';
    const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'V';

    const renderVirtualFrame = () => {
      if (!ctx) return;
      frame++;

      // Modern smooth gradient background
      const grad = ctx.createLinearGradient(0, 0, 1280, 720);
      grad.addColorStop(0, '#090d16');
      grad.addColorStop(0.5, '#0f172a');
      grad.addColorStop(1, '#1e1b4b');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1280, 720);

      // Subtle particle waves
      ctx.save();
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        const yOffset = 360 + Math.sin((frame + i * 40) * 0.03) * 60;
        ctx.strokeStyle = `rgba(99, 102, 241, ${0.15 - i * 0.03})`;
        ctx.lineWidth = 4;
        ctx.arc(640, yOffset, 200 + i * 40, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();

      // Pulsing outer aura
      const pulse = Math.sin(frame * 0.06) * 12;
      const auraGrad = ctx.createRadialGradient(640, 320, 70, 640, 320, 120 + pulse);
      auraGrad.addColorStop(0, 'rgba(99, 102, 241, 0.4)');
      auraGrad.addColorStop(1, 'rgba(99, 102, 241, 0)');
      ctx.fillStyle = auraGrad;
      ctx.beginPath();
      ctx.arc(640, 320, 120 + pulse, 0, Math.PI * 2);
      ctx.fill();

      // Center Avatar Circle
      const circleGrad = ctx.createLinearGradient(560, 240, 720, 400);
      circleGrad.addColorStop(0, '#6366f1');
      circleGrad.addColorStop(1, '#ec4899');
      ctx.fillStyle = circleGrad;
      ctx.beginPath();
      ctx.arc(640, 320, 75, 0, Math.PI * 2);
      ctx.fill();

      // Avatar Initials
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 54px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(initials, 640, 320);

      // Display Name
      ctx.font = 'bold 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(name, 640, 435);

      // Live Virtual Camera Badge
      ctx.fillStyle = '#10b981';
      ctx.font = 'bold 15px monospace';
      ctx.fillText('● LIVE HD VIRTUAL CAMERA', 640, 475);

      // Solveran branding banner at bottom
      ctx.fillStyle = '#64748b';
      ctx.font = '15px monospace';
      ctx.fillText('Powered by Solveran Verve Media Engine • 720p 30 FPS', 640, 660);
    };

    renderVirtualFrame();
    this.virtualVideoTimer = setInterval(renderVirtualFrame, 1000 / 30);

    const stream = (c as any).captureStream ? (c as any).captureStream(30) : null;
    const vTrack: MediaStreamTrack | undefined = stream?.getVideoTracks()[0];
    if (vTrack) {
      this.virtualVideoTrack = vTrack;
      vTrack.addEventListener('ended', () => {
        if (this.virtualVideoTimer) {
          clearInterval(this.virtualVideoTimer);
          this.virtualVideoTimer = null;
        }
      });
      return vTrack;
    }

    throw new Error('Canvas captureStream not supported in this environment');
  }

  private createPresentationCanvasStream(): MediaStream | null {
    try {
      const c = document.createElement('canvas');
      c.width = 1280;
      c.height = 720;
      const ctx = c.getContext('2d');
      if (!ctx) return null;

      let step = 0;
      const drawFrame = () => {
        step++;
        ctx.fillStyle = '#090d16';
        ctx.fillRect(0, 0, 1280, 720);

        // Modern presentation card
        ctx.fillStyle = '#1e293b';
        ctx.roundRect ? ctx.roundRect(40, 40, 1200, 640, 16) : ctx.fillRect(40, 40, 1200, 640);
        ctx.fill();

        // Accent top bar
        const grad = ctx.createLinearGradient(40, 40, 1240, 40);
        grad.addColorStop(0, '#6366f1');
        grad.addColorStop(0.5, '#a855f7');
        grad.addColorStop(1, '#ec4899');
        ctx.fillStyle = grad;
        ctx.fillRect(40, 40, 1200, 8);

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillText('VERVE LIVE SCREEN PRESENTATION', 80, 120);

        ctx.fillStyle = '#94a3b8';
        ctx.font = '22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillText('Dual media presentation stream active • Camera & Audio simultaneous', 80, 175);

        // Animated pulsing indicator
        const alpha = 0.5 + 0.5 * Math.sin(step * 0.1);
        ctx.fillStyle = `rgba(34, 197, 94, ${alpha})`;
        ctx.beginPath();
        ctx.arc(80, 240, 10, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#22c55e';
        ctx.font = 'bold 18px monospace';
        ctx.fillText('STREAMING 1080p • 30 FPS • ENCRYPTED', 105, 246);

        // Workspace branding
        ctx.fillStyle = '#64748b';
        ctx.font = '16px monospace';
        ctx.fillText('Powered by Solveran Verve Media Engine', 80, 630);
      };

      drawFrame();
      const frameTimer = setInterval(drawFrame, 100);
      const stream = (c as any).captureStream ? (c as any).captureStream(30) : null;
      if (stream && stream.getVideoTracks().length > 0) {
        stream.getVideoTracks()[0].addEventListener('ended', () => clearInterval(frameTimer));
        return stream;
      }
      return null;
    } catch (e) {
      console.warn('Failed to create presentation canvas stream:', e);
      return null;
    }
  }

  private createSyntheticAudioTrack(): MediaStreamTrack | null {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return null;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      const dst = ctx.createMediaStreamDestination();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.05, ctx.currentTime); // gentle audible test tone
      osc.connect(gain);
      gain.connect(dst);
      osc.start();
      return dst.stream.getAudioTracks()[0] || null;
    } catch {
      return null;
    }
  }

  public close() {
    if (this.virtualVideoTimer) {
      clearInterval(this.virtualVideoTimer);
      this.virtualVideoTimer = null;
    }
    if (this.localStream) {
      this.localStream.getTracks().forEach(t => t.stop());
      this.localStream = null;
    }
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(t => t.stop());
      this.screenStream = null;
    }
    for (const pc of this.peers.values()) {
      pc.close();
    }
    this.peers.clear();
    this.peerSenders.clear();
    this.pendingCandidates.clear();
    this.offerInFlight.clear();
    this.answerReceived.clear();
    for (const timer of this.offerWatchdog.values()) clearTimeout(timer);
    this.offerWatchdog.clear();
    if (this.sfuPC) {
      this.sfuPC.close();
      this.sfuPC = null;
    }
    this.remoteStreams.clear();
  }
}
